import { DocumentConfiguration } from 'dwt/dist/types/Addon.Camera';

export enum MOBILE_UPLOAD_OPTIONS {
  SCANNER = 'scanner',
  CAMERA = 'camera'
}

class ScannerUtils {
  /**
   * Get the Dynamic Web TWAIN key from the environment variables
   * @returns string
   */
  static getDinamicWebTwainKey = (): string => {
    return process.env.REACT_APP_DYNAMIC_WEB_TWAIN_KEY || '101376608' || '';
  };

  /**
   * Get video configurations for the scanner
   * @param uploadSuccessful
   * @param handleCloseAndReset
   * @param setScannerUIWasClosed
   * @returns DocumentConfiguration
   */
  static getVideoConfigs = ({
    uploadSuccessful,
    handleCloseAndReset,
    setScannerUIWasClosed
  }: {
    uploadSuccessful: boolean;
    handleCloseAndReset: (isSuccess: boolean) => void;
    setScannerUIWasClosed: (value: React.SetStateAction<boolean>) => void;
  }): DocumentConfiguration => {
    const blueColor = '#3959df';
    return {
      scannerViewer: {
        continuousScan: false,
        maxDocuments: 50,
        enableBorderDetection: true,
        headerStyle: {
          selectedColor: blueColor
        },
        footerStyle: {
          selectedColor: blueColor
        },
        resolution: {
          visibility: 'visible',
          defaultValue: { width: 3840, height: 2160 },
          valueList: [
            { label: '720P', value: { width: 1280, height: 720 } },
            { label: '1080P', value: { width: 1920, height: 1080 } },
            { label: '2K', value: { width: 2560, height: 1440 } },
            { label: '4K', value: { width: 3840, height: 2160 } }
          ]
        },
        autoScan: {
          enableAutoScan: false,
          visibility: 'hidden'
        },
        autoDetect: {
          enableAutoDetect: true,
          visibility: 'hidden'
        },
        scanButtonStyle: {
          background: blueColor
        },
        polygonStyle: {
          stroke: blueColor
        },
        funcConfirmExit: (): Promise<boolean> => {
          handleCloseAndReset(uploadSuccessful);
          return Promise.resolve(true);
        }
      },
      documentEditorSettings: {
        defaultViewerName: 'mainViewer',
        cropViewer: {
          polygonStyle: {
            stroke: blueColor
          }
        },
        headerStyle: {
          selectedColor: blueColor
        },
        footerStyle: {
          selectedColor: blueColor
        },
        filter: {
          defaultValue: 'grayscale',
          applyToAll: {
            enableApplyToAll: true
          }
        },
        funcConfirmExitAfterSave: (): void => {
          setScannerUIWasClosed(true);
        }
      }
    };
  };

  /**
   * Get browser name
   * @returns string
   */
  static getBrowserName = (): string => {
    const userAgent = navigator.userAgent;
    let browserName;

    if (userAgent.match(/chrome|chromium|crios/i)) {
      browserName = 'Chrome';
    } else if (userAgent.match(/firefox|fxios/i)) {
      browserName = 'Firefox';
    } else if (userAgent.match(/safari/i)) {
      browserName = 'Safari';
    } else if (userAgent.match(/opr\//i)) {
      browserName = 'Opera';
    } else if (userAgent.match(/edg/i)) {
      browserName = 'Edge';
    } else {
      browserName = 'No browser detection';
    }

    return browserName;
  };

  /**
   * Detects if scanner is being opened in iOS platform
   * @returns true or false
   */
  static isOs = (): boolean => {
    const userAgent = navigator.userAgent;

    if (
      this.getBrowserName() === 'Safari' ||
      userAgent.match(/mac/i) ||
      userAgent.match(/iphone/i) ||
      userAgent.match(/ipad/i)
    ) {
      return true;
    }
    return false;
  };

  /**
   * Detects if scanner is being opened in flutter app
   * @returns true or false
   */
  static isWebInFlutter = (): boolean => {
    return navigator.userAgent.includes('Flutter');
  };
}

export default ScannerUtils;
